import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { locationsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, Empty, ErrorNote, Loading } from '@/components/common/Ui'
import Confirm from '@/components/common/Confirm'
import Drawer from '@/components/common/Drawer'
import { PERMS, can, isSuperAdmin, useAuth } from '@/store/auth'

/**
 * States, and the cities in each — the lists the address pickers offer.
 *
 * A Settings tab rather than its own menu: it is set up once and rarely
 * touched. States on the left; pick one to see and manage its cities.
 *
 * Customers store the names as text, so renaming or deleting here never
 * rewrites an address already saved.
 */
export default function Locations() {
  const { user } = useAuth()
  const editable = can(user, PERMS.settingsWrite) || isSuperAdmin(user)
  const queryClient = useQueryClient()

  const [selectedId, setSelectedId] = useState(null)
  const [filter, setFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  // { kind: 'state' | 'city', id?, name, state_id? }
  const [form, setForm] = useState(null)
  const [confirming, setConfirming] = useState(null) // same shape as form
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const states = useQuery({ queryKey: ['states'], queryFn: locationsApi.states })

  // Opens on Tamil Nadu, where this shop's customers are, with its cities
  // already listed. Once, on first load: after that the selection is the
  // operator's own.
  const defaulted = useRef(false)
  useEffect(() => {
    if (defaulted.current || !states.data) return
    defaulted.current = true
    if (selectedId) return
    const home = states.data.find((st) => st.name.trim().toLowerCase() === 'tamil nadu')
    if (home) setSelectedId(home.id)
  }, [states.data, selectedId])
  const selected = states.data?.find((s) => s.id === selectedId) ?? null

  const cities = useQuery({
    queryKey: ['cities', selectedId],
    queryFn: () => locationsApi.cities(selectedId),
    enabled: Boolean(selectedId),
  })

  const done = (message) => {
    queryClient.invalidateQueries({ queryKey: ['states'] })
    queryClient.invalidateQueries({ queryKey: ['cities'] })
    setForm(null)
    setError('')
    setNotice(message)
  }

  const save = useMutation({
    mutationFn: ({ kind, id, name, state_id }) => {
      if (kind === 'state') {
        return id ? locationsApi.updateState(id, { name }) : locationsApi.createState({ name })
      }
      return id
        ? locationsApi.updateCity(id, { name, state_id })
        : locationsApi.createCity({ name, state_id })
    },
    onSuccess: (row, { kind }) => {
      done(`${kind === 'state' ? 'State' : 'City'} "${row.name}" saved successfully.`)
      // A new state is the one you will want to add cities to next.
      if (kind === 'state') setSelectedId(row.id)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: ({ kind, id }) =>
      kind === 'state' ? locationsApi.removeState(id) : locationsApi.removeCity(id),
    onSuccess: (_, { kind, id, name }) => {
      if (kind === 'state' && id === selectedId) setSelectedId(null)
      done(`${kind === 'state' ? 'State' : 'City'} "${name}" deleted.`)
    },
    onError: (e) => {
      setNotice('')
      setError(errorMessage(e))
    },
  })

  if (states.isLoading) return <Loading />
  if (states.isError) {
    return <ErrorNote message="Could not load states" onRetry={states.refetch} />
  }

  const term = filter.trim().toLowerCase()
  const shownStates = (states.data ?? []).filter((s) => !term || s.name.toLowerCase().includes(term))
  const cityTerm = cityFilter.trim().toLowerCase()
  const shownCities = (cities.data ?? []).filter(
    (c) => !cityTerm || c.name.toLowerCase().includes(cityTerm),
  )

  const openForm = (next) => {
    setNotice('')
    setError('')
    setForm(next)
  }

  const askDelete = (row) => {
    setNotice('')
    setError('')
    setConfirming(row)
  }

  return (
    <div className="locations">
      <p className="hint">
        The states and cities offered when an address is entered. Pick a state to manage its
        cities. Saved addresses keep the names they were saved with.
      </p>

      <Alert tone="success" message={notice} onClose={() => setNotice('')} />
      {!form && <Alert tone="danger" message={error} onClose={() => setError('')} />}

      <div className="locations-grid">
        {/* --- states --- */}
        <section className="panel">
          <div className="panel-title panel-title-row">
            <span>States ({states.data?.length ?? 0})</span>
            {editable && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openForm({ kind: 'state', name: '' })}
              >
                Add state
              </button>
            )}
          </div>
          <input
            type="search"
            className="locations-search"
            placeholder="Find a state"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          {shownStates.length === 0 ? (
            <Empty icon="🗺️" title={term ? 'No state matches' : 'No states yet'} />
          ) : (
            <table className="table compact">
              <tbody>
                {shownStates.map((state) => (
                  <tr
                    key={state.id}
                    className={`locations-row${state.id === selectedId ? ' selected' : ''}`}
                    onClick={() => {
                      setSelectedId(state.id)
                      // A search for one state's city means nothing in the next.
                      if (state.id !== selectedId) setCityFilter('')
                    }}
                  >
                    <td className="strong">{state.name}</td>
                    <td className="right muted nowrap">
                      {state.city_count} {state.city_count === 1 ? 'city' : 'cities'}
                    </td>
                    {editable && (
                      <td className="right nowrap">
                        <div className="row-actions">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              openForm({ kind: 'state', id: state.id, name: state.name })
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={state.city_count > 0 || remove.isPending}
                            title={
                              state.city_count > 0
                                ? `Delete its ${state.city_count} city(ies) first`
                                : undefined
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              askDelete({ kind: 'state', id: state.id, name: state.name })
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* --- cities of the selected state --- */}
        <section className="panel">
          <div className="panel-title panel-title-row">
            <span>
              {selected
                ? `Cities in ${selected.name}${cities.data ? ` (${cities.data.length})` : ''}`
                : 'Cities'}
            </span>
            {editable && selected && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openForm({ kind: 'city', name: '', state_id: selected.id })}
              >
                Add city
              </button>
            )}
          </div>
          {selected && cities.data?.length > 0 && (
            <input
              type="search"
              className="locations-search"
              placeholder={`Find a city in ${selected.name}`}
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            />
          )}
          {!selected && <Empty icon="👈" title="Pick a state to see its cities" />}
          {selected && cities.isLoading && <Loading />}
          {selected && cities.isError && (
            <ErrorNote message="Could not load cities" onRetry={cities.refetch} />
          )}
          {selected && cities.data?.length === 0 && (
            <Empty icon="🏙️" title={`No cities in ${selected.name} yet`} />
          )}
          {selected && cities.data?.length > 0 && shownCities.length === 0 && (
            <Empty icon="🔍" title={`No city in ${selected.name} matches “${cityFilter.trim()}”`} />
          )}
          {selected && shownCities.length > 0 && (
            <table className="table compact">
              <tbody>
                {shownCities.map((city) => (
                  <tr key={city.id}>
                    <td className="strong">{city.name}</td>
                    {editable && (
                      <td className="right nowrap">
                        <div className="row-actions">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                              openForm({ kind: 'city', id: city.id, name: city.name, state_id: city.state_id })
                            }
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={remove.isPending}
                            onClick={() => askDelete({ kind: 'city', id: city.id, name: city.name })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        title={`${form?.id ? 'Edit' : 'Add'} ${form?.kind === 'state' ? 'state' : 'city'}`}
        subtitle={
          form?.kind === 'city' && !form?.id
            ? `In ${states.data?.find((s) => s.id === form?.state_id)?.name ?? ''}`
            : form?.id
              ? form.name
              : undefined
        }
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="location-form"
              className="btn btn-primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <form
          id="location-form"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate(form)
          }}
        >
          {form?.kind === 'city' && (
            <label className="field">
              <span>State *</span>
              <select
                value={form.state_id ?? ''}
                onChange={(e) => setForm({ ...form, state_id: Number(e.target.value) })}
                required
              >
                {(states.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span>{form?.kind === 'state' ? 'State name *' : 'City name *'}</span>
            <input
              autoFocus
              value={form?.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form?.kind === 'state' ? 'Tamil Nadu' : 'Madurai'}
              maxLength={80}
              required
            />
          </label>
          {form?.id && (
            <p className="hint">
              Renaming changes what the pickers offer. Addresses already saved keep the old name.
            </p>
          )}
        </form>
      </Drawer>

      <Confirm
        open={Boolean(confirming)}
        title={`Delete ${confirming?.name ?? ''}?`}
        message={
          confirming?.kind === 'state'
            ? 'It has no cities, so nothing else is removed.'
            : 'It is removed from the city list. Addresses already saved keep the name.'
        }
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming)
          setConfirming(null)
        }}
      />
    </div>
  )
}
